import { Country } from './../model/country.model';
import { PostalAddressService } from './../service/postal-address.service';
import { Component, Input, OnInit, ViewChild, ElementRef, Output, EventEmitter, input } from '@angular/core';
import { Observable, debounceTime, distinctUntilChanged, map, filter, Subject, fromEvent } from 'rxjs';
import { MsgModel } from '../../msg/model/msg.model';
import { State } from '../model/state.model';
import { City } from '../model/city.model';
import { ControlContainer, NgForm, NgModel } from '@angular/forms';

@Component({
    selector: 'input-city-autocomplete',
    templateUrl: './input-city-autocomplete.component.html',
    styleUrls: ['./input-city-autocomplete.component.scss']
})
export class InputCityAutocompleteComponent implements OnInit {
    @Input() id: string = 'City';
    @Input() name: string = 'City';
    @Input() label: string = 'City';
    @Input() placeholder: string = 'City';
    @Input() required: boolean = false;
    @Input() onlyCalifornia: boolean = false;
    @Input() selectedCity: City = { CITY_NAME: '' };
    @Output() selectedCityChange: EventEmitter<City> = new EventEmitter<City>();
    @Input() displayRequiredMessage: boolean = false;

    @Input() state: State | undefined;
    @Output() stateChange: EventEmitter<State> = new EventEmitter<State>();
    @Input() country: Country | undefined;
    @Output() countryChange: EventEmitter<Country> = new EventEmitter<Country>();

    @Input() stateId!: number;
    @Input() countryId!: number;
    @Input() disabled: boolean;

    @ViewChild('searchString') searchString!: NgModel;
    @ViewChild('searchStringInput', { static: true }) searchStringInput!: ElementRef;
    searchStringChanged$: Observable<any> = new Subject<any>();
    inputString: string = '';

    isLoading: boolean = false;
    msg!: MsgModel;

    requiredMsg!: MsgModel;
    minCharacters = 3;

    cities!: City[];
    cityInCalifornia: boolean = false;

    constructor(private postalAddressService: PostalAddressService) {}

    ngOnInit(): void {
        this.requiredMsg = new MsgModel();
        this.requiredMsg.MSG = this.name +  ' is required';
        // Search by ID if given an ID
        if (this.selectedCity.ID! > 0) {
            this.isLoading = true;
            this.searchCityById(this.selectedCity.ID).subscribe((res: any) => {
                this.isLoading = false;
                if (res['city'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    let objCity: City = res['city'];
                    this.cityInCalifornia = objCity.STATE?.STATE_NAME?.toLowerCase() == 'california';
                    this.onSelect(res['city']);
                }
            });
        }
        // Search by ID if CITY_NAME is a number
        else if (this.selectedCity.CITY_NAME && !isNaN(Number(this.selectedCity.CITY_NAME))) {
            this.isLoading = true;
            this.searchCityById(Number(this.selectedCity.CITY_NAME)).subscribe((res: any) => {
                this.isLoading = false;
                if (res['city'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    let objCity: City = res['city'];
                    this.cityInCalifornia = objCity.STATE?.STATE_NAME?.toLowerCase() == 'california';

                    this.onSelect(res['city']);
                }
            });
        }
        // Search by CITY_NAME w/ no ID
        else if (this.selectedCity.CITY_NAME && this.selectedCity.CITY_NAME.length > this.minCharacters) {
            this.isLoading = true;
            this.searchCity(this.selectedCity.CITY_NAME?.trimEnd()).subscribe((res: any) => {
                this.isLoading = false;
                if (res['cities'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    if (res.cities.length === 1) {
                        let objCity: City = res['city'];
                        this.cityInCalifornia = objCity?.STATE?.STATE_NAME?.toLowerCase() == 'california';

                        this.onSelect(res['cities']);
                    } else {
                        let found: boolean = false;
                        for (var i = 0; i < res.cities.length; i++) {
                            if (
                                (res.cities[i].STATE.STATE_NAME === this.selectedCity.STATE?.STATE_NAME &&
                                    res.cities[i].STATE.COUNTRy.COUNTRY_NAME === this.selectedCity.STATE?.COUNTRy?.COUNTRY_NAME) ||
                                (res.cities[i].STATE.STATE_NAME === this.state?.STATE_NAME &&
                                    res.cities[i].STATE.COUNTRy.COUNTRY_NAME === this.country?.COUNTRY_NAME)
                            ) {
                                this.onSelect(res.cities[i]);
                                found = true;
                                break;
                            }
                        }

                        if (!found) {
                            for (var i = 0; i < res.cities.length; i++) {
                                if (
                                    res.cities[i].STATE.STATE_NAME === this.selectedCity.STATE?.STATE_NAME ||
                                    res.cities[i].STATE.STATE_NAME === this.state?.STATE_NAME
                                ) {
                                    this.onSelect(res.cities[i]);
                                    found = true;
                                    break;
                                }
                            }
                        }

                        if (!found) {
                            for (var i = 0; i < res.cities.length; i++) {
                                if (res.cities[i].CITY_NAME === this.selectedCity.CITY_NAME) {
                                    this.onSelect(res.cities[i]);
                                    found = true;
                                    break;
                                }
                            }
                        }
                        let objCity: City = res['cities'][0];
                        this.cityInCalifornia = objCity.STATE?.STATE_NAME?.toLowerCase() == 'california';
                    }
                }
            });
        }

        fromEvent(this.searchStringInput.nativeElement, 'input')
            .pipe(
                // get value
                map((event: any) => {
                    return event.target.value;
                }),
                // only search when value has changed
                distinctUntilChanged(),
                // 500 ms delay before searching
                debounceTime(500),
                // only search if search string is >= 3 characters
                filter((res) => {
                    let canSearch = res !== null && res.length >= this.minCharacters;

                    let newCity = new City();
                    newCity.CITY_NAME = res;
                    this.onSelect(newCity);

                    return canSearch;
                })
            )
            .subscribe((text: string) => {
                this.isLoading = true;
                this.searchCity(text).subscribe((res: any) => {
                    this.isLoading = false;
                    if (res['cities'] == undefined) {
                        let newCity = new City();
                        newCity.CITY_NAME = text;
                        this.onSelect(newCity);
                        this.cities = [];
                    } else {
                        this.cities = res['cities'];
                        // if(this.selectedCity.STATE?.COUNTRIES_ID || 0  >0)
                        //     this.cities=this.cities.filter(x=>x.STATE?.COUNTRIES_ID===1);
                    }

                    let objCity: City = res['cities'][0];
                    this.cityInCalifornia = objCity.STATE?.STATE_NAME?.toLowerCase() == 'california';
                });
            });
    }

    autoSelect(cities?: City[]) {
        if (!cities) {
            return;
        } else if (cities.length === 0) {
            return;
        } else if (cities.length === 1) {
            for (var i = 0; i < cities.length; i++) {
                if (cities[i].CITY_NAME === this.selectedCity.CITY_NAME) {
                    this.onSelect(cities[i]);
                    break;
                }
            }
        }
    }

    searchCity(searchString: any): Observable<City[]> {
        return this.postalAddressService.searchCities(searchString, this.countryId, this.stateId);
    }

    searchCityById(id: any): Observable<City[]> {
        return this.postalAddressService.searchCitiesById(id);
    }

    onSelect(city: City | City[]) {
        // Accept either a single City or an array of City; when an array is passed pick the first element.
        let selected: City | undefined;
        if (Array.isArray(city)) {
            selected = city.length > 0 ? city[0] : undefined;
        } else {
            selected = city;
        }
        if (!selected) {
            // no selection available, clear and return
            this.selectedCity = { CITY_NAME: '' } as City;
            this.clearAutocompleteArray();
            return;
        }
        this.selectedCity = selected;
        this.selectedCityChange.emit(this.selectedCity);

        if (this.selectedCity.STATE) {
            this.state = JSON.parse(JSON.stringify(this.selectedCity.STATE));
            this.stateChange.emit(this.state);
        }

        if (this.selectedCity.STATE?.COUNTRy) {
            this.country = JSON.parse(JSON.stringify(this.selectedCity.STATE?.COUNTRy));
            this.countryChange.emit(this.country);
        }

        this.searchString.control.setValue(this.selectedCity.CITY_NAME);
        this.checkForId(this.searchString);

        this.clearAutocompleteArray();
    }

    clearAutocompleteArray() {
        this.cities = [];
    }

    checkForId(input: NgModel) {
        if (this.selectedCity.CITY_NAME && this.selectedCity.CITY_NAME.length === 0) {
            input.control.setErrors({ Required: 'New City will be created' });
        }
        if (!this.selectedCity.ID) {
            input.control.setErrors({ idRequired: 'New City will be created' });
        } else {
            input.control.setErrors(null);
        }
    }
}
